/**
 * Price Chart Component
 * Implements PRD FR-33
 */

import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { useSimulationStore } from '../state/simulationStore';

export function PriceChart() {
  const { simulation } = useSimulationStore();

  if (!simulation) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 h-80">
        <div className="flex items-center justify-center h-full text-gray-500">
          Run a simulation to see the price chart
        </div>
      </div>
    );
  }

  const { equilibrium, params, shocks } = simulation;
  const { S1, S2, S3, liquidityDiscount } = equilibrium;
  const { mu } = params;

  // Chart data
  const data = [
    {
      t: 1,
      transactionPrice: S1,
      fundamentalValue: mu,
      efficientPrice: mu,
      label: 't=1',
    },
    {
      t: 2,
      transactionPrice: S2,
      fundamentalValue: mu + shocks.epsilon2,
      efficientPrice: mu + shocks.epsilon2,
      label: 't=2',
    },
    {
      t: 3,
      transactionPrice: null, // No trading at t=3
      fundamentalValue: S3,
      efficientPrice: null,
      label: 't=3',
    },
  ];

  // Calculate Y-axis domain
  const allPrices = [S1, S2, S3, mu].filter((p) => p !== null) as number[];
  const minPrice = Math.min(...allPrices) - 2;
  const maxPrice = Math.max(...allPrices) + 2;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Price Evolution</h3>

      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: '#9ca3af' }}
          />
          <YAxis
            domain={[minPrice, maxPrice]}
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: '#9ca3af' }}
            tickFormatter={(value) => value.toFixed(2)}
          />
          <Tooltip
            formatter={(value: number) => value?.toFixed(4) ?? 'N/A'}
            labelFormatter={(label) => `Time: ${label}`}
            contentStyle={{
              backgroundColor: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
            }}
          />
          <Legend />

          {/* Expected value reference line */}
          <ReferenceLine
            y={mu}
            stroke="#9ca3af"
            strokeDasharray="5 5"
            label={{
              value: `μ = ${mu}`,
              position: 'right',
              fontSize: 11,
              fill: '#6b7280',
            }}
          />

          {/* Fundamental value path */}
          <Line
            type="monotone"
            dataKey="fundamentalValue"
            stroke="#22c55e"
            strokeWidth={2}
            dot={{ r: 5, fill: '#22c55e' }}
            name="Fundamental Value"
            connectNulls
          />

          {/* Transaction price */}
          <Line
            type="monotone"
            dataKey="transactionPrice"
            stroke="#3b82f6"
            strokeWidth={3}
            dot={{ r: 6, fill: '#3b82f6' }}
            name="Transaction Price"
            connectNulls
          />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Liquidity Discount Annotation */}
      <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-amber-800">
            Liquidity Discount at t=1:
          </span>
          <span className="font-mono font-bold text-amber-900">
            {liquidityDiscount.toFixed(4)}
          </span>
        </div>
        <div className="text-xs text-amber-600 mt-1">
          S₁ = {S1.toFixed(4)} vs μ = {mu.toFixed(2)}
        </div>
      </div>
    </div>
  );
}
